// Linear, allocation-light SQL tokenizer used by context-derivation and
// procedural-locals extraction. Emits keyword vs identifier classification
// via the `SQL_KEYWORDS` set in `keywords.ts` and skips strings/comments
// using `sourceScan.ts`. Quoted identifiers (`[..]`, `".."`, `` `..` ``)
// surface as a single `ident` token whose `text` includes the quotes — the
// keyword classification only inspects `text.toUpperCase()`, so retaining
// the quotes is harmless.

import type { DatabaseEngineId } from '../../../core/engines';

import {
  isBareIdentChar,
  isBareIdentStart,
  isOperatorChar,
  skipBacktickId,
  skipBlockComment,
  skipBracketedId,
  skipDoubleQuoted,
  skipSingleQuoted,
  skipToEol,
  tryDollarQuoted,
} from './sourceScan';
import { SQL_KEYWORDS } from './keywords';

export type TokenKind =
  | 'kw'
  | 'ident'
  | 'op'
  | 'lparen'
  | 'rparen'
  | 'comma'
  | 'dot'
  | 'literal'
  | 'semicolon';

export type Token = {
  readonly type: TokenKind;
  readonly text: string;
  readonly start: number;
  readonly end: number;
};

export function tokenize(
  source: string,
  from: number,
  to: number,
  engineId: DatabaseEngineId,
): Token[] {
  const out: Token[] = [];
  let i = from;
  while (i < to) {
    const c = source.charCodeAt(i);
    if (c === 0x20 || c === 0x09 || c === 0x0a || c === 0x0d) {
      i += 1;
      continue;
    }
    if (c === 0x2d /* - */ && source.charCodeAt(i + 1) === 0x2d) {
      i = skipToEol(source, i + 2, to);
      continue;
    }
    if (engineId === 'mysql' && c === 0x23 /* # */) {
      i = skipToEol(source, i + 1, to);
      continue;
    }
    if (c === 0x2f /* / */ && source.charCodeAt(i + 1) === 0x2a /* * */) {
      i = skipBlockComment(source, i + 2, to);
      continue;
    }
    if (c === 0x27 /* ' */) {
      const end = skipSingleQuoted(source, i + 1, to);
      out.push({ type: 'literal', text: source.slice(i, end), start: i, end });
      i = end;
      continue;
    }
    if (engineId === 'pgsql' && c === 0x24 /* $ */) {
      const end = tryDollarQuoted(source, i, to);
      if (end !== -1) {
        out.push({ type: 'literal', text: source.slice(i, end), start: i, end });
        i = end;
        continue;
      }
    }
    if ((engineId === 'mssql' || engineId === 'sqlite') && c === 0x5b /* [ */) {
      const end = skipBracketedId(source, i + 1, to);
      out.push({ type: 'ident', text: source.slice(i, end), start: i, end });
      i = end;
      continue;
    }
    if (engineId === 'mysql' && c === 0x60 /* ` */) {
      const end = skipBacktickId(source, i + 1, to);
      out.push({ type: 'ident', text: source.slice(i, end), start: i, end });
      i = end;
      continue;
    }
    if (c === 0x22 /* " */) {
      const end = skipDoubleQuoted(source, i + 1, to);
      out.push({ type: 'ident', text: source.slice(i, end), start: i, end });
      i = end;
      continue;
    }
    if (c === 0x28) {
      out.push({ type: 'lparen', text: '(', start: i, end: i + 1 });
      i += 1;
      continue;
    }
    if (c === 0x29) {
      out.push({ type: 'rparen', text: ')', start: i, end: i + 1 });
      i += 1;
      continue;
    }
    if (c === 0x2c) {
      out.push({ type: 'comma', text: ',', start: i, end: i + 1 });
      i += 1;
      continue;
    }
    if (c === 0x2e) {
      out.push({ type: 'dot', text: '.', start: i, end: i + 1 });
      i += 1;
      continue;
    }
    if (c === 0x3b) {
      out.push({ type: 'semicolon', text: ';', start: i, end: i + 1 });
      i += 1;
      continue;
    }
    if (c >= 0x30 && c <= 0x39) {
      const end = consumeNumber(source, i, to);
      out.push({ type: 'literal', text: source.slice(i, end), start: i, end });
      i = end;
      continue;
    }
    if (isBareIdentStart(c)) {
      const end = consumeBareIdentifier(source, i, to);
      const text = source.slice(i, end);
      const isKeyword = SQL_KEYWORDS.has(text.toUpperCase());
      out.push({ type: isKeyword ? 'kw' : 'ident', text, start: i, end });
      i = end;
      continue;
    }
    if (isOperatorChar(c)) {
      let end = i + 1;
      while (end < to && isOperatorChar(source.charCodeAt(end))) end += 1;
      out.push({ type: 'op', text: source.slice(i, end), start: i, end });
      i = end;
      continue;
    }
    // Anything we don't recognize — advance and continue.
    i += 1;
  }
  return out;
}

/**
 * Drop a trailing bare word (identifier / keyword) the caret is ATTACHED to.
 * The half-typed word under the caret is the ANSWER the user is part-way
 * through, never part of the slot's own context: `DECLARE @x IN▮` is the same
 * data-type slot as `DECLARE @x ▮`, and `CAST(x AS NVA▮)` the same CAST
 * target as `CAST(x AS ▮)`. Position detectors that read the token stream's
 * TAIL must therefore run against the dropped stream, or every slot they
 * recognise silently disappears the moment the user types a character.
 *
 * Twin of `dropAttachedPartial` in
 * `@/features/sql-grammar/document/editorContextSlotShared` (same rule, the
 * grammar projection's `ClauseToken` stream). Dropping cannot turn a NAME
 * position into a type one: after the drop the tail is the `(` / `,` /
 * `COLUMN` / verb that made it a name position to begin with.
 */
export function dropAttachedPartialTokens(
  tokens: readonly Token[],
  cursor: number,
): readonly Token[] {
  const last = tokens[tokens.length - 1];
  if (!last || last.end !== cursor) return tokens;
  if (last.type !== 'ident' && last.type !== 'kw') return tokens;
  return tokens.slice(0, -1);
}

function consumeBareIdentifier(source: string, from: number, to: number): number {
  let i = from + 1;
  while (i < to && isBareIdentChar(source.charCodeAt(i))) i += 1;
  return i;
}

function consumeNumber(source: string, from: number, to: number): number {
  let i = from + 1;
  while (i < to) {
    const c = source.charCodeAt(i);
    if ((c >= 0x30 && c <= 0x39) || c === 0x2e || c === 0x65 || c === 0x45) {
      i += 1;
    } else break;
  }
  return i;
}
